import { useContext, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "../../next-i18next";
import { Routes } from "../../utils/types/routes";
import { ApolloContext } from "../../context/ApolloClientProvider";
import { hasAuthErrorParam } from "../../utils/auth0/authSessionHint";
import { useAuthSessionResume } from "../../utils/auth0/useAuthSessionResume";
import { RedirectContainer } from "./Redirect/RedirectContainer";
export var RedirectToStep = function () {
    var t = useTranslation().t;
    var router = useRouter();
    var _a = useAuth0(), user = _a.user, isLoading = _a.isLoading, loginWithRedirect = _a.loginWithRedirect;
    var _b = useState(false), showLoading = _b[0], setShowLoading = _b[1];
    var _c = useState(false), showRedirect = _c[0], setShowRedirect = _c[1];
    var doesClientHaveAuth = useContext(ApolloContext).doesClientHaveAuth;
    var _d = useAuthSessionResume(), hydrated = _d.hydrated, canResume = _d.canResume, isResuming = _d.isResuming, beginResume = _d.beginResume;
    // Both guest exits below navigate away for good, but the effect can re-run
    // before the transition commits (`beginResume` is rebuilt whenever asPath
    // changes), so fire each at most once. Mirrors Auth's guestRedirectStartedRef.
    var navigationStartedRef = useRef(false);
    useEffect(function () {
        if (!router.isReady || isLoading || !hydrated) {
            return;
        }
        // A failed resume leaves error params behind; wait for them to be scrubbed
        // so this redirect does not race the cleanup.
        if (hasAuthErrorParam(router.query)) {
            return;
        }
        if (!user) {
            if (router.query.redirect === "sign-in") {
                if (navigationStartedRef.current) {
                    return;
                }
                navigationStartedRef.current = true;
                loginWithRedirect({
                    authorizationParams: {
                        login_hint: router.query.email,
                    },
                });
                return;
            }
            if (canResume) {
                setShowLoading(true);
                beginResume();
                return;
            }
            if (isResuming) {
                setShowLoading(true);
                return;
            }
            if (navigationStartedRef.current) {
                return;
            }
            navigationStartedRef.current = true;
            router.push({
                pathname: Routes.StartLoan,
                query: router.query,
            });
            return;
        }
        setShowLoading(false);
        setShowRedirect(true);
    }, [
        user,
        router,
        isLoading,
        loginWithRedirect,
        hydrated,
        canResume,
        isResuming,
        beginResume,
    ]);
    if (showLoading || (isLoading && (canResume || isResuming))) {
        return <div>{t("Loading")}</div>;
    }
    if (showRedirect && doesClientHaveAuth) {
        return <RedirectContainer />;
    }
    return null;
};
//# sourceMappingURL=data:application/json;base64,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